<template>
  <div
    ref="menubarRef"
    class="menubar-container flex flex-column"
    :class="{ 'menu-dragging': isDragging }"
  >
    <div class="col-12 p-0 flex flex-column" @click="handleMenubarClick">
      <Menubar
        ref="primeMenubarRef"
        :model="toolbar"
        :auto-display="true"
      />
    </div>
  </div>
  <div class="pl-3 pr-3" v-if="filteredToolbar.length">
    <div
      v-for="panel in filteredToolbar"
      :key="panel.panelId"
      class="menu-panel-wrapper"
    >
      <div
        class="menu-drop-container"
        :class="{
          'menu-drop-container--visible': panel.panelId === currentPanelId,
          'menu-drop-container--active': isDragging && panel.panelId === currentPanelId,
        }"
      >
        <div class="menu-drop-container__label">
          <div class="menu-drop-container__icon">
            <i class="fa-solid fa-grip" />
          </div>
          <div class="menu-drop-container__text">
            <span class="menu-drop-container__title">{{ t('statsEditor.placeholders.menuTitle') }}</span>
            <span class="menu-drop-container__subtitle">{{ t('statsEditor.placeholders.menuHelper') }}</span>
          </div>
        </div>
        <draggable
          :list="panel.children"
          :disabled="panel.panelId !== currentPanelId || !enabled"
          item-key="name"
          class="menu-drop-container__list block grid"
          handle=".handle"
          v-bind="dragOptions"
          :data-menu-render="JSON.stringify({ rowId, columnId, panel })"
        >
          <template #item="{ element }">
            <div
              class="root-row m-4"
              style="position: relative;"
              :class="{ 'not-draggable': !enabled }"
            >
              <StatsBuilderSubRow
                :row-parent-id="rowId"
                :column-parent-id="columnId"
                :panel-id="panel.panelId"
                :row-data="element"
              />
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
} from 'vue';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import Menubar from 'primevue/menubar';
import draggable from 'vuedraggable';
import StatsBuilderSubRow from '@/components/stats-builder/StatsBuilderSubRow.vue';
import { setActiveMenubarItem } from '@/helpers';
import { useStore } from '@/store';
import { uniqueIdS6Generator } from '@/store/modules/stats-editor';
import {
  StatsBuilderColumnLayoutEnum,
  StatsBuilderRowLayoutEnum,
  StatsWidgets,
} from '@/types';

interface ToolbarItem {
  panelId?: string;
  items?: ToolbarItem[];
  children?: any[];
  class?: string;
  _baseClass?: string;
  label?: string | Record<string, string>;
  command?: (event: any) => void;
}

interface PanelItem extends ToolbarItem {
  panelId: string;
  children: any[];
}

export default defineComponent({
  name: 'StatsWidgetMenuRender',
  components: {
    draggable,
    StatsBuilderSubRow,
    Menubar,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    rowId: {
      type: String,
      required: true,
    },

    columnId: {
      type: String,
      required: true,
    },

    editMode: {
      type: Boolean,
      required: true,
    },

    widgetData: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  setup(props: { editMode: boolean; widgetData: any }) {
    const { t, locale } = useI18n();
    const store = useStore();
    const isDragging = computed(() => store.getters['statsEditor/getIsDraggingBuilderItem']);

    const ACTIVE_MENU_CLASS = 'current-active-menu';
    const MENU_ITEM_PANEL_CLASS_PREFIX = 'menu-item-panel-';
    const BODY_DRAGGING_CLASS = 'stats-menu-dragging';

    const widgetDataRef = ref(props.widgetData);
    const filteredToolbar: Ref<PanelItem[]> = ref([]);
    const refreshFilteredToolbar = ref(true);
    const currentPanelId = ref('');
    const toolbar: Ref<ToolbarItem[]> = ref([]);

    const menubarRef = ref<HTMLElement | null>(null);
    const primeMenubarRef = ref();

    const enabled = ref(true);
    const dragOptions = ref({
      animation: 200,
      group: { name: 'line_builder', pull: false, put: ['line_element', 'widget_element', 'statsWidget'] },
      disabled: false,
      onStart: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', true);
      },
      onEnd: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', false);
      },
      onAdd: async (evt: any) => {
        const rawWidget = evt.item?.getAttribute('data-widget') || evt.clone?.getAttribute('data-widget');
        if (!rawWidget) return;

        const dataWidget = JSON.parse(rawWidget);
        if ([StatsWidgets.MENU, StatsWidgets.TABS].includes(dataWidget.type)) return;

        const menuRenderData = JSON.parse(evt.to.getAttribute('data-menu-render'));

        // Remove widget from source location
        const rawLocation = evt.item?.getAttribute('data-widget-location') || evt.clone?.getAttribute('data-widget-location');
        const rawSubLocation = evt.item?.getAttribute('data-location') || evt.clone?.getAttribute('data-location');
        if (rawLocation) {
          store.dispatch('statsEditor/removeWidget', { location: JSON.parse(rawLocation) });
        } else if (rawSubLocation) {
          store.dispatch('statsEditor/removeWidgetFromSubCol', { dataLastLocation: JSON.parse(rawSubLocation) });
        }

        // Create a sub-row/sub-column with the widget inside
        const columnId = `column_${uniqueIdS6Generator()}`;
        const subRowId = `row_${uniqueIdS6Generator()}`;
        const rowData = {
          id: subRowId,
          type: 'StatsWidgetRow',
          layout: StatsBuilderRowLayoutEnum.TWELVE,
          children: [{
            id: columnId,
            type: 'StatsWidgetColumn',
            layout: StatsBuilderColumnLayoutEnum.TWELVE,
            widgets: [dataWidget],
          }],
        };

        await nextTick();
        store.dispatch('statsEditor/addRowToIndex', {
          rowData,
          index: evt.newIndex || 0,
          panelId: menuRenderData.panel.panelId,
          parentRowId: menuRenderData.rowId,
          parentColumnId: menuRenderData.columnId,
        });

        // Clean up any raw item vuedraggable may have added to panel.children
        await nextTick();
        const panel = filteredToolbar.value.find(
          (p: PanelItem) => p.panelId === menuRenderData.panel.panelId,
        );
        if (panel) {
          panel.children = panel.children.filter(
            (child: any) => child.type !== dataWidget.type || child.id,
          );
        }
      },
    });

    const normalizeClasses = (value?: string) => (value ? value.split(' ').filter(Boolean) : []);
    const buildClassString = (classes: string[]) => Array.from(new Set(classes.filter(Boolean))).join(' ');

    const ensurePanelBaseClass = (menuItem: ToolbarItem, panelId?: string) => {
      const baseClasses = normalizeClasses(menuItem._baseClass || menuItem.class);
      const sanitized = baseClasses.filter((className) => className !== ACTIVE_MENU_CLASS
        && !className.startsWith(MENU_ITEM_PANEL_CLASS_PREFIX));
      if (panelId) {
        sanitized.push(`${MENU_ITEM_PANEL_CLASS_PREFIX}${panelId}`);
      }
      // eslint-disable-next-line no-param-reassign
      menuItem._baseClass = buildClassString(sanitized);
      // eslint-disable-next-line no-param-reassign
      menuItem.class = menuItem._baseClass;
    };

    const applyActiveClass = (menuItem: ToolbarItem, isActive: boolean) => {
      const base = buildClassString(normalizeClasses(menuItem._baseClass || menuItem.class)
        .filter((className) => className !== ACTIVE_MENU_CLASS));
      const classes = normalizeClasses(base);
      if (isActive) {
        classes.push(ACTIVE_MENU_CLASS);
      }
      // eslint-disable-next-line no-param-reassign
      menuItem.class = buildClassString(classes);
    };

    const resolveMenubarRoot = () => {
      if (menubarRef.value instanceof HTMLElement) {
        return menubarRef.value;
      }
      if (primeMenubarRef.value && primeMenubarRef.value.$el) {
        return primeMenubarRef.value.$el as HTMLElement;
      }
      return null;
    };

    const highlightDomMenuItem = (panelId: string) => {
      if (!panelId || typeof document === 'undefined') {
        return;
      }
      const selector = [
        `.p-menubar .p-menuitem.${MENU_ITEM_PANEL_CLASS_PREFIX}${panelId} .p-menuitem-link`,
        `.p-menubar .p-menuitem[class*="${MENU_ITEM_PANEL_CLASS_PREFIX}${panelId}"] .p-menuitem-link`,
        `.p-menubar-overlay .p-menuitem.${MENU_ITEM_PANEL_CLASS_PREFIX}${panelId} .p-menuitem-link`,
        `.p-menubar-overlay .p-menuitem[class*="${MENU_ITEM_PANEL_CLASS_PREFIX}${panelId}"] .p-menuitem-link`,
      ].join(', ');
      const link = document.querySelector(selector) as HTMLElement | null;
      if (link) {
        setActiveMenubarItem(link);
      }
    };

    const selectActiveMenu = (panelId: string) => {
      if (!panelId || panelId === currentPanelId.value) {
        return;
      }
      currentPanelId.value = panelId;
      highlightDomMenuItem(panelId);
    };

    const resolveMenuItemFromTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return null;
      }
      // Essayer plusieurs sélecteurs pour trouver l'élément de menu
      const selectors = [
        `.p-menuitem[class*="${MENU_ITEM_PANEL_CLASS_PREFIX}"]`,
        '.p-menuitem',
        '.p-menubar-item',
        '[role="menuitem"]'
      ];

      for (const selector of selectors) {
        const menuItem = target.closest(selector) as HTMLElement | null;
        if (menuItem) {
          return menuItem;
        }
      }
      return null;
    };

    const resolvePanelIdFromMenuItem = (menuItem: HTMLElement | null) => {
      if (!menuItem) {
        return '';
      }
      const panelClass = Array.from(menuItem.classList).find((className) => className.startsWith(MENU_ITEM_PANEL_CLASS_PREFIX));
      if (!panelClass) {
        return '';
      }
      return panelClass.replace(MENU_ITEM_PANEL_CLASS_PREFIX, '');
    };

    const activateMenuItem = (menuItem: HTMLElement | null) => {
      const panelId = resolvePanelIdFromMenuItem(menuItem);
      if (!panelId) {
        return;
      }
      selectActiveMenu(panelId);
    };

    const resolveMenuItemFromPoint = (x: number, y: number) => {
      if (typeof document === 'undefined') {
        return null;
      }
      const elements = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [];
      if (elements.length) {
        for (const element of elements) {
          const menuItem = resolveMenuItemFromTarget(element);
          if (menuItem) {
            return menuItem;
          }
        }
      }
      const fallback = document.elementFromPoint ? document.elementFromPoint(x, y) : null;
      return resolveMenuItemFromTarget(fallback);
    };

    let hoverTimeout: NodeJS.Timeout | null = null;
    let lastHoveredMenuItem: HTMLElement | null = null;

    const simulateMouseEvents = (menuItem: HTMLElement) => {
      if (!menuItem) return;


      // Créer des événements avec plus de propriétés
      const eventOptions = {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: 0,
        clientY: 0,
        button: 0,
        buttons: 0,
        relatedTarget: null,
      };

      // Créer et dispatcher différents types d'événements
      const mouseEnterEvent = new MouseEvent('mouseenter', eventOptions);
      const mouseOverEvent = new MouseEvent('mouseover', eventOptions);
      const focusEvent = new FocusEvent('focus', { bubbles: true, cancelable: true });

      // Dispatcher sur l'élément de menu principal
      menuItem.dispatchEvent(mouseEnterEvent);
      menuItem.dispatchEvent(mouseOverEvent);

      // Dispatcher sur le lien à l'intérieur si présent
      const menuLink = menuItem.querySelector('.p-menuitem-link') as HTMLElement;
      if (menuLink) {
        menuLink.dispatchEvent(mouseEnterEvent);
        menuLink.dispatchEvent(mouseOverEvent);
        menuLink.dispatchEvent(focusEvent);

        // Essayer de déclencher le focus
        menuLink.focus();
      }

      // Essayer de déclencher l'état hover via CSS
      menuItem.classList.add('p-menuitem-active');
      if (menuLink) {
        menuLink.classList.add('p-focus');
      }

      // Chercher et activer le submenu directement - VERSION PLUS AGRESSIVE
      const submenu = menuItem.querySelector('.p-submenu-list') as HTMLElement;
      if (submenu) {
        submenu.style.display = 'block';
        submenu.style.visibility = 'visible';
        submenu.style.opacity = '1';
        submenu.style.pointerEvents = 'auto';
        submenu.style.zIndex = '1000';
      }

      // Essayer aussi de déclencher l'ouverture via PrimeVue
      const menuItemContent = menuItem.querySelector('.p-menuitem-content') as HTMLElement;
      if (menuItemContent) {
        menuItemContent.dispatchEvent(mouseEnterEvent);
        menuItemContent.dispatchEvent(mouseOverEvent);
      }

      // Forcer l'activation du menu parent si c'est un sous-menu
      const parentMenu = menuItem.closest('.p-menubar, .p-menu') as HTMLElement;
      if (parentMenu) {
        parentMenu.classList.add('p-menubar-active');
      }
    };

    const handleMenuHover = (event: Event) => {
      if (!isDragging.value) {
        return;
      }
      const targetMenuItem = resolveMenuItemFromTarget(event.target);
      let menuItemToProcess = targetMenuItem;

      if (!menuItemToProcess) {
        const pointerEvent = event as DragEvent | PointerEvent | MouseEvent;
        if (
          typeof pointerEvent.clientX === 'number'
          && typeof pointerEvent.clientY === 'number'
        ) {
          menuItemToProcess = resolveMenuItemFromPoint(pointerEvent.clientX, pointerEvent.clientY);
        }
      }

      if (menuItemToProcess) {
        // Si c'est un nouvel élément de menu
        if (lastHoveredMenuItem !== menuItemToProcess) {
          // Annuler le timeout précédent
          if (hoverTimeout) {
            clearTimeout(hoverTimeout);
          }

          lastHoveredMenuItem = menuItemToProcess;

          // Activer immédiatement le panel
          activateMenuItem(menuItemToProcess);

          // Simuler les événements de survol avec un délai pour les sous-menus
          hoverTimeout = setTimeout(() => {
            if (isDragging.value && lastHoveredMenuItem === menuItemToProcess && menuItemToProcess) {
              simulateMouseEvents(menuItemToProcess);
            }
          }, 300); // Délai de 300ms pour éviter l'ouverture trop rapide
        }
      } else {
        // Réinitialiser si on n'est plus sur un élément de menu
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        lastHoveredMenuItem = null;
      }
    };

    const addBodyDragClass = () => {
      if (typeof document === 'undefined') {
        return;
      }
      document.body.classList.add(BODY_DRAGGING_CLASS);
    };

    const removeBodyDragClass = () => {
      if (typeof document === 'undefined') {
        return;
      }
      document.body.classList.remove(BODY_DRAGGING_CLASS);
    };

    let hoverListenersAttached = false;

    const attachGlobalHoverListeners = () => {
      if (typeof document === 'undefined' || hoverListenersAttached) {
        return;
      }
      document.addEventListener('pointerover', handleMenuHover, true);
      document.addEventListener('dragenter', handleMenuHover, true);
      document.addEventListener('dragover', handleMenuHover, true);
      hoverListenersAttached = true;
    };

    const detachGlobalHoverListeners = () => {
      if (typeof document === 'undefined' || !hoverListenersAttached) {
        return;
      }
      document.removeEventListener('pointerover', handleMenuHover, true);
      document.removeEventListener('dragenter', handleMenuHover, true);
      document.removeEventListener('dragover', handleMenuHover, true);
      hoverListenersAttached = false;
    };

    const cleanupHoverState = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      lastHoveredMenuItem = null;
    };

    const cleanupOpenedSubmenus = () => {
      if (typeof document === 'undefined') {
        return;
      }

      // Supprimer toutes les classes CSS ajoutées pendant le drag
      const activeMenuItems = document.querySelectorAll('.p-menuitem-active');
      activeMenuItems.forEach((item) => {
        item.classList.remove('p-menuitem-active');
      });

      const focusedLinks = document.querySelectorAll('.p-menuitem-link.p-focus');
      focusedLinks.forEach((link) => {
        link.classList.remove('p-focus');
      });

      // Fermer tous les sous-menus ouverts manuellement
      const openSubmenus = document.querySelectorAll('.p-submenu-list');
      openSubmenus.forEach((submenu: Element) => {
        const submenuElement = submenu as HTMLElement;
        // Réinitialiser les styles forcés
        submenuElement.style.display = '';
        submenuElement.style.visibility = '';
        submenuElement.style.opacity = '';
      });

      // Approche plus agressive : simuler un clic ailleurs pour fermer tous les menus
      setTimeout(() => {
        // Créer un clic sur le body pour fermer tous les overlays PrimeVue
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });

        // Cliquer sur un élément neutre (pas sur un menu)
        const body = document.body;
        if (body) {
          body.dispatchEvent(clickEvent);
        }

        // Forcer la fermeture des overlays PrimeVue
        const overlays = document.querySelectorAll('.p-menubar-overlay, .p-overlay-visible');
        overlays.forEach((overlay: Element) => {
          const overlayElement = overlay as HTMLElement;
          overlayElement.style.display = 'none';
          overlayElement.classList.remove('p-overlay-visible');
        });

        // Simuler un événement mouseleave sur tous les éléments de menu
        const allMenuItems = document.querySelectorAll('.p-menuitem');
        allMenuItems.forEach((menuItem) => {
          const mouseLeaveEvent = new MouseEvent('mouseleave', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          menuItem.dispatchEvent(mouseLeaveEvent);

          const menuLink = menuItem.querySelector('.p-menuitem-link');
          if (menuLink) {
            menuLink.dispatchEvent(mouseLeaveEvent);
            // Supprimer le focus
            if (menuLink instanceof HTMLElement) {
              menuLink.blur();
            }
          }
        });
      }, 100); // Petit délai pour laisser le temps au drag de se terminer
    };

    const formatToolbarLabels = (items: ToolbarItem[]) => {
      items.forEach((item) => {
        if (item.label && typeof item.label !== 'string' && item.label[locale.value]) {
          // eslint-disable-next-line no-param-reassign
          item.label = item.label[locale.value];
        }
        if (item.items) {
          formatToolbarLabels(item.items);
        }
      });
    };

    const prepareToolbarForView = (items: ToolbarItem[], panels: PanelItem[]) => {
      items.forEach((item) => {
        if (item.panelId) {
          ensurePanelBaseClass(item, item.panelId);
          // eslint-disable-next-line no-param-reassign
          item.command = (event: any) => {
            const target = event.originalEvent?.target as HTMLElement | null;
            if (target) {
              setActiveMenubarItem(target);
            }
            selectActiveMenu(item.panelId as string);
          };
          const cloned = JSON.parse(JSON.stringify(item));
          panels.push({
            ...cloned,
            panelId: item.panelId,
            children: Array.isArray(cloned.children) ? cloned.children : [],
          });
        }
        if (item.items) {
          prepareToolbarForView(item.items, panels);
        }
      });
    };

    const prepareToolbarForEdit = (items: ToolbarItem[], panels: PanelItem[]) => {
      items.forEach((item) => {
        // Traiter d'abord les panneaux avant de supprimer les panelId
        if (item.panelId) {
          ensurePanelBaseClass(item, item.panelId);
          // eslint-disable-next-line no-param-reassign
          item.command = (event: any) => {
            const target = event.originalEvent?.target as HTMLElement | null;
            if (target) {
              setActiveMenubarItem(target);
            }
            selectActiveMenu(item.panelId as string);
          };
          const cloned = JSON.parse(JSON.stringify(item));
          panels.push({
            ...cloned,
            panelId: item.panelId,
            children: Array.isArray(cloned.children) ? cloned.children : [],
          });
        }

        if (item.items && !item.items.length) {
          // eslint-disable-next-line no-param-reassign
          delete item.items;
        } else if (item.items) {
          // Ne plus supprimer le panelId pour les éléments avec sous-menus
          // Cela permet de conserver les panneaux même avec des sous-menus
          if (item.children) {
            // eslint-disable-next-line no-param-reassign
            delete item.children;
          }
          if (item.command) {
            // eslint-disable-next-line no-param-reassign
            delete item.command;
          }
          prepareToolbarForEdit(item.items, panels);
        }
      });
    };

    const addPlaceholderRowToWidgetData = (data: ToolbarItem[], concernedPanel: PanelItem) => {
      data.forEach((item) => {
        if (item.panelId && item.panelId === concernedPanel.panelId) {
          // eslint-disable-next-line no-param-reassign
          item.children = concernedPanel.children;
        }
        if (item.items && item.items.length) {
          addPlaceholderRowToWidgetData(item.items, concernedPanel);
        }
      });
    };

    const addClassToPanel = (menuItems: ToolbarItem[], panelId: string) => {
      let found = false;
      menuItems.forEach((menuItem) => {
        if (menuItem.panelId) {
          ensurePanelBaseClass(menuItem, menuItem.panelId);
        } else {
          ensurePanelBaseClass(menuItem);
        }

        let isActive = menuItem.panelId === panelId;
        if (menuItem.items && menuItem.items.length > 0) {
          const childFound = addClassToPanel(menuItem.items, panelId);
          isActive = isActive || childFound;
          if (childFound) {
            found = true;
          }
        }

        applyActiveClass(menuItem, isActive);
        if (isActive && menuItem.panelId === panelId) {
          found = true;
        }
      });
      return found;
    };

    const refreshActiveMenuClass = async () => {
      await nextTick();
      const menubarElement = resolveMenubarRoot();
      if (menubarElement) {
        const menuItems = menubarElement.querySelectorAll('.current-active-menu, [active-item="true"]');
        menuItems.forEach((menuItem) => {
          const element = menuItem as HTMLElement;
          element.classList.remove('current-active-menu');
          element.removeAttribute('active-item');
        });
      }
      addClassToPanel(toolbar.value, currentPanelId.value);
      await nextTick();
      highlightDomMenuItem(currentPanelId.value);
    };

    const rebuildToolbar = async () => {
      filteredToolbar.value = [];
      toolbar.value = JSON.parse(JSON.stringify(props.widgetData?.toolbar || []));

      formatToolbarLabels(toolbar.value);

      if (props.editMode) {
        prepareToolbarForEdit(toolbar.value, filteredToolbar.value);
      } else {
        prepareToolbarForView(toolbar.value, filteredToolbar.value);
      }

      filteredToolbar.value = filteredToolbar.value.map((panel) => ({
        ...panel,
        children: Array.isArray(panel.children) ? panel.children : [],
      }));

      const activePanel = filteredToolbar.value.find((panel) => panel.panelId === currentPanelId.value)
        || filteredToolbar.value[0];
      currentPanelId.value = activePanel ? activePanel.panelId : '';

      await refreshActiveMenuClass();
    };

    watch(() => props.widgetData, async () => {
      widgetDataRef.value = props.widgetData;
      if (refreshFilteredToolbar.value) {
        await rebuildToolbar();
      } else {
        refreshFilteredToolbar.value = true;
      }
    }, { deep: true });

    watch(filteredToolbar, async () => {
      const concernedPanel = filteredToolbar.value.find((item) => item.children
        && item.children.find((child: any) => child.type === 'placeholder'));
      if (concernedPanel) {
        refreshFilteredToolbar.value = false;
        if (widgetDataRef.value?.toolbar) {
          addPlaceholderRowToWidgetData(widgetDataRef.value.toolbar, concernedPanel);
          formatToolbarLabels(widgetDataRef.value.toolbar);
        }
      }
      await refreshActiveMenuClass();
    }, { deep: true });

    watch(currentPanelId, async () => {
      await refreshActiveMenuClass();
    });

    onBeforeMount(async () => {
      await rebuildToolbar();
    });

    onMounted(async () => {
      await refreshActiveMenuClass();
    });

    onBeforeUnmount(() => {
      detachGlobalHoverListeners();
      removeBodyDragClass();
      cleanupHoverState();
      cleanupOpenedSubmenus();
    });

    watch(isDragging, (draggingActive) => {
      if (draggingActive) {
        attachGlobalHoverListeners();
        addBodyDragClass();
        highlightDomMenuItem(currentPanelId.value);
      } else {
        detachGlobalHoverListeners();
        removeBodyDragClass();
        cleanupHoverState();
        cleanupOpenedSubmenus();
      }
    });

    const handleMenubarClick = (event: Event) => {
      // Empêcher la propagation du clic vers le parent pour éviter l'ouverture du panel de configuration
      event.stopPropagation();
    };

    return {
      t,
      enabled,
      dragOptions,
      filteredToolbar,
      currentPanelId,
      toolbar,
      menubarRef,
      primeMenubarRef,
      isDragging,
      handleMenubarClick,
    };
  },
});
</script>

<style scoped lang="scss">
.menu-panel-wrapper {
  position: relative;
}

.menu-drop-container {
  display: none;
  position: relative;
  padding: 1.25rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, rgba(148, 200, 64, 0.12), rgba(148, 200, 64, 0.03));
  border: 1px solid rgba(148, 200, 64, 0.3);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
  overflow: hidden;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -55% -40% auto;
    height: 160%;
    background: radial-gradient(circle at 20% 20%, rgba(148, 200, 64, 0.4), transparent 55%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &__label {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 0.85rem;
    z-index: 1;
  }

  &__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.9rem;
    background: rgba(148, 200, 64, 0.18);
    color: #94c840;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 24px -16px rgba(148, 200, 64, 0.6);
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;

    i {
      color: currentColor;
      font-size: 1.1rem;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2f353b;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: rgba(73, 80, 87, 0.7);
    line-height: 1.3;
    max-width: 20rem;
  }

  &__list {
    min-height: 4.5rem;
    padding: 0.85rem;
    border-radius: 0.9rem;
    border: 1px dashed rgba(148, 200, 64, 0.45);
    background: rgba(255, 255, 255, 0.65);
    transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  }
}

.menu-drop-container--visible {
  display: block;
  animation: menuDropFadeIn 0.18s ease;
}

.menu-drop-container--active {
  border-color: rgba(148, 200, 64, 0.6);
  background: linear-gradient(135deg, rgba(148, 200, 64, 0.28), rgba(148, 200, 64, 0.1));
  box-shadow: 0 26px 48px -26px rgba(148, 200, 64, 0.55), 0 0 0 1px rgba(148, 200, 64, 0.3);

  &::before {
    opacity: 1;
    animation: menuDropGlow 1.6s ease infinite;
  }

  .menu-drop-container__icon {
    background: #94c840;
    color: #ffffff;
    transform: translateY(-2px);
  }

  .menu-drop-container__list {
    border-style: solid;
    border-color: rgba(148, 200, 64, 0.6);
    background: rgba(148, 200, 64, 0.12);
    box-shadow: 0 0 0 1px rgba(148, 200, 64, 0.25);
  }
}

.menu-dragging .p-menubar {
  border-color: rgba(148, 200, 64, 0.45);
  box-shadow: 0 0 0 2px rgba(148, 200, 64, 0.25);
  border-radius: 0.75rem;
}

.menu-dragging .p-menuitem.current-active-menu > .p-menuitem-content,
.menu-dragging .p-menuitem[active-item="true"] > .p-menuitem-content {
  background-color: rgba(148, 200, 64, 0.18);
  border-radius: 0.6rem;
}

:global(body.stats-menu-dragging .p-menubar-overlay) {
  border-color: rgba(148, 200, 64, 0.45);
  box-shadow: 0 0 0 2px rgba(148, 200, 64, 0.25);
  border-radius: 0.75rem;
}

:global(body.stats-menu-dragging .p-menubar-overlay .p-menuitem.current-active-menu > .p-menuitem-content),
:global(body.stats-menu-dragging .p-menubar-overlay .p-menuitem[active-item="true"] > .p-menuitem-content) {
  background-color: rgba(148, 200, 64, 0.18);
  border-radius: 0.6rem;
}

@keyframes menuDropFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes menuDropGlow {
  0%,
  100% {
    opacity: 0.2;
  }

  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-drop-container,
  .menu-drop-container::before,
  .menu-drop-container--visible,
  .menu-drop-container--active,
  .menu-drop-container__icon {
    transition: none !important;
    animation: none !important;
  }
}
</style>
